import React, { useCallback, useEffect, useState } from "react";
import { useContextStateManagement } from "../../context/ContextApi";
import TicketDetailsDialog from "../TicketDetailsDialog/TicketDetailsDialog";
import "./TicketsPage.css";

const STATUSES = ["open", "in_progress", "closed"];

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  closed: "Closed",
};

const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

const TICKET_TYPE_OPTIONS = [
  { value: "Fix", label: "Fix" },
  { value: "New Build", label: "New Build" },
];

const ORGANIZATION_KEYS = [
  "organizationGroupKey",
  "organizationName",
  "organization",
  "organizationId",
  "orgName",
  "orgId",
];

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

const getTicketTypeLabel = (value) =>
  TICKET_TYPE_OPTIONS.find((option) => option.value === value)?.label || "Fix";

const getTicketTypeToneClass = (value) =>
  String(value || "Fix").trim().toLowerCase().replace(/\s+/g, "-");

const defaultPriorityStats = () => ({
  high: 0,
  medium: 0,
  low: 0,
});

const buildPriorityStatsFromTickets = (tickets) =>
  tickets.reduce(
    (stats, ticket) => ({
      ...stats,
      [ticket.priority]: (stats[ticket.priority] || 0) + 1,
    }),
    defaultPriorityStats(),
  );

const TicketsPage = () => {
  const {
    getAllTickets,
    getTicketById,
    updateTicketStatus,
    updateTicketType,
    addTicketComment,
    sendTicketEmail,
    getTicketAssigneeSuggestions,
    assignTicket,
    userType,
  } = useContextStateManagement();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draggingTicketId, setDraggingTicketId] = useState(null);
  const [activeColumn, setActiveColumn] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [priorityStatsByStatus, setPriorityStatsByStatus] = useState({});
  const [assigneeSuggestions, setAssigneeSuggestions] = useState([]);
  const [openAssignmentOnDialog, setOpenAssignmentOnDialog] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const adminMode = userType === "admin";

  const applyBoardResponse = useCallback((data) => {
    const nextTickets = Array.isArray(data) ? data : data?.tickets || [];
    const nextStats =
      !Array.isArray(data) && data?.priorityStatsByStatus
        ? data.priorityStatsByStatus
        : {};

    setTickets(nextTickets);
    setPriorityStatsByStatus(nextStats);
  }, []);

  const loadTickets = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) {
          setLoading(true);
        }
        setError("");
        const data = await getAllTickets({ scope: "board", includeStats: true });
        applyBoardResponse(data);
      } catch (requestError) {
        setError(
          requestError.message || "An error occurred while processing your request",
        );
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [applyBoardResponse, getAllTickets],
  );

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    let ignore = false;

    const loadAssigneeSuggestions = async () => {
      if (!adminMode) {
        if (!ignore) {
          setAssigneeSuggestions([]);
        }
        return;
      }

      try {
        const data = await getTicketAssigneeSuggestions();
        if (ignore) {
          return;
        }
        setAssigneeSuggestions(Array.isArray(data) ? data : []);
      } catch (requestError) {
        if (!ignore) {
          setAssigneeSuggestions([]);
        }
      }
    };

    loadAssigneeSuggestions();

    return () => {
      ignore = true;
    };
  }, [adminMode, getTicketAssigneeSuggestions]);

  const refreshBoardInBackground = () => {
    loadTickets(false);
  };

  const updateTicketInState = useCallback((updatedTicket) => {
    setTickets((currentValue) =>
      currentValue.map((ticket) =>
        ticket.ticketId === updatedTicket.ticketId ? updatedTicket : ticket,
      ),
    );
    setSelectedTicket((currentValue) =>
      currentValue?.ticketId === updatedTicket.ticketId
        ? updatedTicket
        : currentValue,
    );
  }, []);

  const markOpenedTicketReadLocally = useCallback(
    (ticket) => {
      if (!adminMode || !ticket?.ticketId) {
        return ticket;
      }

      const openedTicket = {
        ...ticket,
        isUnread: false,
        lastViewedByAdmin: new Date().toISOString(),
      };
      updateTicketInState(openedTicket);
      return openedTicket;
    },
    [adminMode, updateTicketInState],
  );

  const handleTicketOpen = async (ticket, options = {}) => {
    setDialogOpen(true);
    setSelectedTicket(markOpenedTicketReadLocally(ticket));
    setOpenAssignmentOnDialog(Boolean(options.openAssignmentEditor));

    try {
      const hydratedTicket = await getTicketById(ticket.ticketId, {
        scope: "board",
      });
      updateTicketInState(hydratedTicket);
      refreshBoardInBackground();
    } catch (requestError) {
      setError(
        requestError.message || "An error occurred while processing your request",
      );
    }
  };

  const handleAssigneeOpen = (ticket) => {
    handleTicketOpen(ticket, { openAssignmentEditor: true });
  };

  const handleStatusChange = async (ticketId, status) => {
    if (!adminMode) {
      return null;
    }
    const updatedTicket = await updateTicketStatus(ticketId, status, {
      scope: "board",
    });
    updateTicketInState(updatedTicket);
    refreshBoardInBackground();
    return updatedTicket;
  };

  const handleTicketTypeChange = async (ticketId, type) => {
    if (!adminMode) {
      return null;
    }

    const updatedTicket = await updateTicketType(ticketId, type, {
      scope: "board",
    });
    updateTicketInState(updatedTicket);
    refreshBoardInBackground();
    return updatedTicket;
  };

  const handleDragStart = (ticketId) => {
    setDraggingTicketId(ticketId);
  };

  const handleDrop = async (status) => {
    if (!adminMode || !draggingTicketId) {
      return;
    }

    const ticket = tickets.find((item) => item.ticketId === draggingTicketId);
    setActiveColumn("");
    setDraggingTicketId(null);

    if (!ticket || ticket.status === status) {
      return;
    }

    try {
      await handleStatusChange(ticket.ticketId, status);
    } catch (requestError) {
      setError(
        requestError.message || "An error occurred while processing your request",
      );
    }
  };

  const handleCommentSubmit = async (commentText) => {
    if (!adminMode || !selectedTicket) {
      return null;
    }

    const updatedTicket = await addTicketComment(
      selectedTicket.ticketId,
      commentText,
      { scope: "board" },
    );
    updateTicketInState(updatedTicket);
    refreshBoardInBackground();
    return updatedTicket;
  };

  const toggleOrganizationGroup = (status, groupKey) => {
    const stateKey = `${status}:${groupKey}`;
    setCollapsedGroups((currentValue) => ({
      ...currentValue,
      [stateKey]: !currentValue[stateKey],
    }));
  };

  const handleEmailSend = async (payload) => {
    if (!adminMode || !selectedTicket) {
      return null;
    }

    const updatedTicket = await sendTicketEmail(
      selectedTicket.ticketId,
      payload,
      { scope: "board" },
    );
    updateTicketInState(updatedTicket);
    refreshBoardInBackground();
    return updatedTicket;
  };

  const handleAssigneeSave = async (payload) => {
    if (!adminMode || !selectedTicket) {
      return null;
    }

    const updatedTicket = await assignTicket(
      selectedTicket.ticketId,
      payload,
      { scope: "board" },
    );
    updateTicketInState(updatedTicket);
    refreshBoardInBackground();

    const nextSuggestionEmail = String(
      payload?.assigneeEmail || payload?.email || "",
    )
      .trim()
      .toLowerCase();

    if (nextSuggestionEmail) {
      setAssigneeSuggestions((currentValue) => {
        if (
          currentValue.some(
            (entry) =>
              String(entry.email || "").trim().toLowerCase() === nextSuggestionEmail,
          )
        ) {
          return currentValue;
        }

        const displayName = String(
          payload?.assigneeName || payload?.displayName || "",
        ).trim();

        return [
          {
            email: nextSuggestionEmail,
            displayName,
            label: displayName || nextSuggestionEmail,
          },
          ...currentValue,
        ];
      });
    }

    return updatedTicket;
  };

  const getOrganizationGroup = (ticket) => {
    if (ticket?.organizationGroupKey && ticket?.organizationLabel) {
      return {
        key: ticket.organizationGroupKey,
        label: ticket.organizationLabel,
      };
    }

    const matchedKey = ORGANIZATION_KEYS.find((key) => {
      const value = ticket?.[key];
      return value !== null && value !== undefined && String(value).trim();
    });

    if (!matchedKey) {
      return {
        key: "unassigned",
        label: "Unassigned Organization",
      };
    }

    const rawValue = String(ticket[matchedKey]).trim();
    return {
      key: `${matchedKey}:${rawValue.toLowerCase()}`,
      label: rawValue,
    };
  };

  const assigneeFilterOptions = Array.from(
    new Set(
      tickets
        .map((ticket) => ticket.assignedToLabel)
        .filter((label) => label && label.trim()),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const PRIORITY_SORT_RANK = { high: 0, medium: 1, low: 2 };
  const sortTickets = (list) =>
    [...list].sort((a, b) => {
      if (sortBy === "priority") {
        const rankA = PRIORITY_SORT_RANK[a.priority || "medium"] ?? 1;
        const rankB = PRIORITY_SORT_RANK[b.priority || "medium"] ?? 1;
        if (rankA !== rankB) return rankA - rankB;
      }
      const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return sortBy === "oldest" ? dateA - dateB : dateB - dateA;
    });

  const filteredTickets = sortTickets(
    tickets.filter((ticket) => {
      if (priorityFilter !== "all" && (ticket.priority || "medium") !== priorityFilter) {
        return false;
      }
      if (assigneeFilter !== "all" && (ticket.assignedToLabel || "") !== assigneeFilter) {
        return false;
      }
      return true;
    }),
  );

  const groupedTickets = STATUSES.reduce((result, status) => {
    const statusTickets = filteredTickets.filter((ticket) => ticket.status === status);
    const groupsMap = new Map();

    statusTickets.forEach((ticket) => {
      const organizationGroup = getOrganizationGroup(ticket);
      const currentGroup = groupsMap.get(organizationGroup.key) || {
        ...organizationGroup,
        tickets: [],
      };
      currentGroup.tickets.push(ticket);
      groupsMap.set(organizationGroup.key, currentGroup);
    });

    result[status] = Array.from(groupsMap.values()).map((group) => ({
      ...group,
      priorityStats:
        priorityStatsByStatus?.[status]?.[group.key] ||
        buildPriorityStatsFromTickets(group.tickets),
    }));
    return result;
  }, {});

  return (
    <div className="tickets-board-shell">
      <div className="support-page-header">
        <h1>Support Tickets Board</h1>
        <p>
          Manage and track all customer support requests. Drag tickets to update
          their status.
        </p>
      </div>

      {error ? (
        <div className="support-feedback support-feedback--error">{error}</div>
      ) : null}

      <div className="tickets-instruction-bar">
        Drag and drop tickets between columns to update their status. Click any
        ticket to view details and add comments.
      </div>

      <div className="tickets-toolbar">
        <label className="tickets-toolbar-field">
          Sort by
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="priority">Priority (High first)</option>
          </select>
        </label>
        <label className="tickets-toolbar-field">
          Priority
          <select
            value={priorityFilter}
            onChange={(event) => setPriorityFilter(event.target.value)}
          >
            <option value="all">All priorities</option>
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="tickets-toolbar-field">
          Assignee
          <select
            value={assigneeFilter}
            onChange={(event) => setAssigneeFilter(event.target.value)}
          >
            <option value="all">All assignees</option>
            {assigneeFilterOptions.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="support-empty-state">Loading tickets...</div>
      ) : (
        <div className="tickets-board-grid">
          {STATUSES.map((status) => (
            <section
              key={status}
              className={`tickets-column tickets-column--${status} ${
                activeColumn === status ? "is-active-drop-zone" : ""
              }`}
              onDragOver={
                adminMode
                  ? (event) => {
                      event.preventDefault();
                      setActiveColumn(status);
                    }
                  : undefined
              }
              onDragLeave={
                adminMode
                  ? () => {
                      if (activeColumn === status) {
                        setActiveColumn("");
                      }
                    }
                  : undefined
              }
              onDrop={
                adminMode
                  ? (event) => {
                      event.preventDefault();
                      handleDrop(status);
                    }
                  : undefined
              }
            >
              <header className={`tickets-column-header status-${status}`}>
                <div className="tickets-column-title-row">
                  <span className="tickets-column-dot" />
                  <h2>{STATUS_LABELS[status]}</h2>
                </div>
                <div className={`tickets-count-badge status-${status}`}>
                  {filteredTickets.filter((ticket) => ticket.status === status).length || 0}
                </div>
              </header>

              <div className="tickets-column-body">
                {groupedTickets[status]?.length ? (
                  groupedTickets[status].map((group, groupIndex) => (
                    <section
                      key={group.key}
                      className="tickets-organization-group"
                    >
                      <button
                        type="button"
                        className="tickets-organization-group-header"
                        onClick={() => toggleOrganizationGroup(status, group.key)}
                      >
                        <span className="tickets-organization-group-copy">
                          <span>{group.label}</span>
                          <span className="tickets-organization-priority-stats">
                            <span className="priority-high">{`High: ${group.priorityStats?.high || 0}`}</span>
                            <span className="priority-medium">{`Medium: ${group.priorityStats?.medium || 0}`}</span>
                            <span className="priority-low">{`Low: ${group.priorityStats?.low || 0}`}</span>
                          </span>
                        </span>
                        <span className="tickets-organization-group-meta">
                          <span>{group.tickets.length}</span>
                          <span className="tickets-organization-group-chevron">
                            {collapsedGroups[`${status}:${group.key}`] ? "▸" : "▾"}
                          </span>
                        </span>
                      </button>
                      {!collapsedGroups[`${status}:${group.key}`] ? (
                        <div className="tickets-organization-group-list">
                          {group.tickets.map((ticket, index) => (
                            <button
                              type="button"
                              key={ticket.ticketId}
                              className="tickets-board-card"
                              draggable={adminMode}
                              onDragStart={
                                adminMode
                                  ? () => handleDragStart(ticket.ticketId)
                                  : undefined
                              }
                              onDragEnd={
                                adminMode
                                  ? () => {
                                      setDraggingTicketId(null);
                                      setActiveColumn("");
                                    }
                                  : undefined
                              }
                              onClick={() => handleTicketOpen(ticket)}
                              style={{
                                "--ticket-delay": `${(groupIndex + index) * 0.14}s`,
                              }}
                            >
                              <div className="tickets-board-card-bar" />
                              <div className="tickets-board-card-content">
                                <div className="tickets-board-card-top">
                                  <div>
                                    <div className="tickets-card-title-row">
                                      <h3>{ticket.issue || ticket.title || "-"}</h3>
                                      {ticket.isUnread ? (
                                        <span className="ticket-unread-pill">
                                          Unread
                                        </span>
                                      ) : null}
                                    </div>
                                    <p>{ticket.requesterName || ticket.email || "-"}</p>
                                    {adminMode ? (
                                      <div
                                        className="ticket-type-controls"
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        <div
                                          className="ticket-assignee-inline ticket-assignee-inline--interactive"
                                          role="button"
                                          tabIndex={0}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleAssigneeOpen(ticket);
                                          }}
                                          onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              handleAssigneeOpen(ticket);
                                            }
                                          }}
                                        >
                                          <span className="ticket-assignee-label">
                                            Assign To
                                          </span>
                                          <span
                                            className={`ticket-assignee-pill ${
                                              ticket.assignedToLabel
                                                ? ""
                                                : "ticket-assignee-pill--empty"
                                            }`}
                                          >
                                            {ticket.assignedToLabel || "Unassigned"}
                                          </span>
                                        </div>
                                        <div className="ticket-type-inline">
                                          <span
                                            className={`ticket-type-pill ticket-type-pill--${getTicketTypeToneClass(
                                              ticket.ticketType || ticket.type,
                                            )}`}
                                          >
                                            {getTicketTypeLabel(
                                              ticket.ticketType || ticket.type,
                                            )}
                                          </span>
                                          <select
                                            className="ticket-type-select"
                                            value={ticket.ticketType || ticket.type || "Fix"}
                                            onChange={(event) =>
                                              handleTicketTypeChange(
                                                ticket.ticketId,
                                                event.target.value,
                                              )
                                            }
                                          >
                                            {TICKET_TYPE_OPTIONS.map((option) => (
                                              <option
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className={`ticket-avatar status-${status}`}>
                                    {(ticket.requesterName || ticket.email || "ST")
                                      .trim()
                                      .slice(0, 2)
                                      .toUpperCase()}
                                  </div>
                                </div>
                                <div className="tickets-board-card-bottom">
                                  <span>{formatDate(ticket.updatedAt || ticket.createdAt)}</span>
                                  <div className="ticket-badge-row">
                                    <div
                                      className={`ticket-priority-badge priority-${
                                        ticket.priority || "medium"
                                      }`}
                                    >
                                      {PRIORITY_LABELS[ticket.priority] || "Medium"}
                                    </div>
                                    <div className={`ticket-status-badge status-${ticket.status}`}>
                                      {STATUS_LABELS[ticket.status] || ticket.status || "-"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </section>
                  ))
                ) : (
                  <div className="support-empty-state support-empty-state--compact">
                    No tickets in this column.
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      <TicketDetailsDialog
        open={dialogOpen}
        ticket={selectedTicket}
        adminMode={adminMode}
        assigneeSuggestions={assigneeSuggestions}
        openAssignmentOnOpen={openAssignmentOnDialog}
        onOpenAssignmentHandled={() => setOpenAssignmentOnDialog(false)}
        onClose={() => setDialogOpen(false)}
        onStatusChange={(status) =>
          selectedTicket ? handleStatusChange(selectedTicket.ticketId, status) : null
        }
        onCommentSubmit={handleCommentSubmit}
        onEmailSend={handleEmailSend}
        onAssignTicket={handleAssigneeSave}
        onTicketTypeChange={(nextType) =>
          selectedTicket
            ? handleTicketTypeChange(selectedTicket.ticketId, nextType)
            : null
        }
      />
    </div>
  );
};

export default TicketsPage;

